import { Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ApprovalFlowStep } from '@app/components/approval-status-flow/approval-status-flow.component';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { Candidate } from '@app/models/offer-letters/candidate.model';
import { OfferLetter } from '@app/models/offer-letters/offer-letter.model';
import { createCosignerApprovalFlowSteps } from '@app/modules/offer-letter/functions/create-cosigner-approval-flow-steps';
import { getOfferLetterApprovalFlowSteps } from '@app/modules/offer-letter/functions/offer-letter-approval-flow-steps';
import { OfferLettersTable } from '@app/modules/offer-letter/services';
import {
    CANDIDATE_SHOW,
    OfferLetterCreationReferer,
} from '@app/modules/offer-letter/types/offer-letter-referer-view.type';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService, AuthService, FileHelperService, NotifyService } from '@app/services';
import { TemplateSlotNamesDisplayOptions } from '@app/types/translatables/template-slot-names-display.options';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

@Component({
    templateUrl: './candidate-show.template.html',
    styleUrls: ['./candidate-show.style.scss'],
})
export class CandidateShowView implements OnInit {
    isLoading = false;

    modifyNoteModalTitle: 'Edit Note' | 'Add Note' = 'Add Note';

    candidate: Candidate;
    latestOfferLetter: OfferLetter;
    employee: Employee;

    openWithdrawOfferModal = false;
    openCandidateDeclinedModal = false;
    openDeleteCandidateModal = false;
    openHireCandidateWarningModal = false;

    showWithdrawOfferButton = true;
    showCandidateDeclinedButton = true;
    showRevertButton = false;
    showHireCandidateButton = false;
    showReviseButton = false;
    canHireCandidate = true;

    offerLetterTable: OfferLettersTable;
    candidateApprovalFlowSteps: ApprovalFlowStep[] = [];
    cosignerApprovalFlowSteps: ApprovalFlowStep[] = [];

    signingTemplateIsDeleted = false;

    offerLetterHasCosigners = false;

    templateSlotNamesDisplayOptions = TemplateSlotNamesDisplayOptions;

    constructor(
        public auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private fileHelper: FileHelperService,
        private analyticsService: AnalyticService,
        private translateService: TranslateService
    ) {
        this.candidate = this.route.snapshot.data.models.get('candidate');
        this.employee = this.auth.employee;
        this.latestOfferLetter = this.candidate.latestOfferLetter;
        this.signingTemplateIsDeleted = !this.latestOfferLetter.signingTemplate;
        this.offerLetterTable = new OfferLettersTable(this.auth, this.candidate, this.translateService);
        this.setMatMenuButtons();
        this.setOfferLetterApprovalFlowSteps();
        this.canHireCandidate = this.canUserAccessCandidate();

        if (!this.candidate.isHired()) {
            this.showReviseButton = true;
        }

        this.translateService.onLangChange
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.setOfferLetterApprovalFlowSteps());
    }

    async ngOnInit(): Promise<void> {
        this.offerLetterHasCosigners = this.latestOfferLetter.hasCosigners();
    }

    async onTableMenuEvent(event: MenuColumnEvent): Promise<void> {
        const eventType = event.menuItem.event;
        const selectedOfferLetter = event.data as OfferLetter;

        switch (eventType) {
            case 'download':
                this.onDownloadSelectedOfferLetter(selectedOfferLetter);
                break;
        }
    }

    onHireCandidate(): void {
        if (!this.latestOfferLetter.isSigned()) {
            this.openHireCandidateWarningModal = true;

            return;
        }

        this.sendToHireFlow();
    }

    sendToHireFlow(): void {
        this.openHireCandidateWarningModal = false;
        const offerLetterSignedAt = this.candidate.latestOfferLetter.signedAt;

        setTimeout(() => {
            this.router.navigate(['/', 'employees', 'directory', 'create'], {
                queryParams: {
                    candidateId: this.candidate.id,
                    offerLetterSignedAt,
                },
            });
        });
    }

    setModifyNoteModalTitle(): void {
        this.modifyNoteModalTitle = this.candidate.note ? 'Edit Note' : 'Add Note';
    }

    onReviseOfferLetter(): void {
        const referer: OfferLetterCreationReferer = CANDIDATE_SHOW;
        const refererQueryParams = { queryParams: { referer } };

        this.router.navigate(
            ['/employees', 'offer-letters', String(this.candidate.latestOfferLetter.id), 'revise'],
            refererQueryParams
        );
    }

    async onRevert(): Promise<void> {
        this.isLoading = true;
        this.candidate.latestOfferLetter.revertChanges();

        try {
            await this.candidate.latestOfferLetter.save();
            this.notify.success('Reverted status on offer letter.');
            this.setMatMenuButtons();
            this.offerLetterTable.reload();

            this.setOfferLetterApprovalFlowSteps();
        } catch (err) {
            this.notify.error(err.message || 'Could not revert status on offer letter');
        }
        this.isLoading = false;
    }

    async onCancelNote(): Promise<void> {
        this.candidate.revert();
    }

    async onModifyNote(): Promise<void> {
        this.isLoading = true;

        const originalNote = this.candidate.getOriginalAttribute('note');

        try {
            await this.candidate.save();
            this.notify.success('Updated candidate.');
            originalNote
                ? this.analyticsService.trackEvent(AnalyticEvents.OfferLetterEditCandidateNote)
                : this.analyticsService.trackEvent(AnalyticEvents.OfferLetterAddCandidateNote);
        } catch (err) {
            this.notify.error(err.message || 'Could not save the note');
        }
        this.isLoading = false;
    }

    async onWithdrawOfferLetter(): Promise<void> {
        this.isLoading = true;

        this.candidate.latestOfferLetter.withdrawnAt = moment(new Date()).toDate();

        try {
            await this.candidate.latestOfferLetter.save();
            this.notify.success('Withdrew offer letter.');
            this.setMatMenuButtons();
            this.offerLetterTable.reload();
            this.analyticsService.trackEvent(AnalyticEvents.OfferLetterWithdrawOfferLetter);
            this.setOfferLetterApprovalFlowSteps();
        } catch (err) {
            this.notify.error(err.message || 'Could not update the offer letter');
        }
        this.isLoading = false;
    }

    async onDecline(): Promise<void> {
        this.isLoading = true;

        this.candidate.latestOfferLetter.declinedAt = moment(new Date()).toDate();

        try {
            await this.candidate.latestOfferLetter.save();
            this.notify.success('Offer letter was updated.');
            this.setMatMenuButtons();
            this.offerLetterTable.reload();
            this.analyticsService.trackEvent(AnalyticEvents.OfferLetterDeclinedOfferLetter);
            this.setOfferLetterApprovalFlowSteps();
        } catch (err) {
            this.notify.error(err.message || 'Could not update the offer letter');
        }
        this.isLoading = false;
    }

    async onDeleteCandidate(): Promise<void> {
        this.isLoading = true;

        try {
            await this.candidate.delete();
            this.notify.success('Deleted candidate.');

            this.router.navigate(['/employees', 'offer-letters', 'candidates']);
        } catch (err) {
            this.notify.error(err.message || 'Could not delete the candidate');
        }
        this.isLoading = false;
    }

    async onRemindIndividual(index: number): Promise<void> {
        try {
            await this.candidate.latestOfferLetter?.signingTemplate?.document?.notify(
                this.candidate.latestOfferLetter.cosigners[index],
                false
            );

            this.notify.success('document.reminderSent');
        } catch (err) {
            this.notify.error(err);
        }
    }

    private setMatMenuButtons(): void {
        if (this.candidate.latestOfferLetter.declinedAt || this.candidate.latestOfferLetter.withdrawnAt) {
            this.showWithdrawOfferButton = false;
            this.showCandidateDeclinedButton = false;

            this.showRevertButton = true;
            return;
        }

        // Default
        this.showWithdrawOfferButton = true;
        this.showCandidateDeclinedButton = true;

        this.showRevertButton = false;
    }

    private setOfferLetterApprovalFlowSteps(): void {
        this.candidateApprovalFlowSteps = getOfferLetterApprovalFlowSteps(
            this.candidate,
            this.candidate.latestOfferLetter
        );

        if (this.latestOfferLetter.hasCosigners()) {
            this.cosignerApprovalFlowSteps = createCosignerApprovalFlowSteps(
                this.latestOfferLetter,
                this.translateService.currentLang
            );
        }

        this.showHireCandidateButton = this.candidate.isHireable();
    }

    private onDownloadSelectedOfferLetter(selectedOfferLetter: OfferLetter): void {
        this.fileHelper.save(selectedOfferLetter.signatureRequest.fileId, selectedOfferLetter.filename, 'pdf');
    }

    private canUserAccessCandidate(): boolean {
        // If current user does not have permission to access the candidate, disable the hire button
        // We do this because the lack of permission will cause the Hire flow to fail on Salary creation.
        return this.auth.can('manageAll') || this.latestOfferLetter.reportsTo?.id === this.employee.id;
    }
}
